import React, {useState, useEffect, useCallback, useRef} from 'react';
import {
  View,
  Text,
  TextInput,
  StyleSheet,
  TouchableOpacity,
  SafeAreaView,
  Platform,
  ActivityIndicator,
  Pressable,
  KeyboardAvoidingView,
  ScrollView,
  Linking,
  AppState,
  Keyboard,
} from 'react-native';
import {useAuth} from '../../contexts/AuthContext';
import {getBiometryDisplayName} from '../../services/biometrics';
import { COLORS } from '../../theme/themeTokens';
import PasswordInput from '../../components/UIComponents/PasswordInput';
import {API_BASE_URL} from '../../config/env';
import { COLORS_WRONG } from '../../theme/colorsWrong';
import {hasAcceptedTerms, setHasAcceptedTerms} from '../../services/auth';
import DeviceInfo from 'react-native-device-info';
import LoginLockoutScreen from './LoginLockoutScreen';
import {reportLoginLockout} from '../../services/loginLockoutReporter';
import {
  DEFAULT_LOGIN_LOCKOUT_STATE,
  MAX_LOGIN_ATTEMPTS,
  applyCountedLoginFailure,
  clearLoginLockoutState,
  getRemainingLockoutSeconds,
  isCountedLoginError,
  loadLoginLockoutState,
  persistLoginLockoutState,
  type LoginLockoutNotificationStatus,
  type LoginLockoutState,
} from '../../services/loginLockout';

const FONTS = {
  title: Platform.select({
    ios: 'HelveticaNeue',
    android: 'sans-serif',
  }),
  body: Platform.select({
    ios: 'System',
    android: 'sans-serif',
  }),
};

const formatRemainingAttempts = (remainingAttempts: number): string =>
  `Incorrect login or password. You have ${remainingAttempts} ${
    remainingAttempts === 1 ? 'attempt' : 'attempts'
  } remaining.`;

interface LoginScreenProps {
  onForgotPassword: () => void;
}

import {
  getLoginPrefill,
  saveCredentialsForPrefill,
  saveLoginDraft,
} from '../../services/loginPrefill';

export const LoginScreen: React.FC<LoginScreenProps> = ({
  onForgotPassword,
}) => {
  const prefill = getLoginPrefill();
  const [loginInput, setLoginInput] = useState(prefill.login);
  const [password, setPassword] = useState(prefill.password);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [termsAlreadyAccepted, setTermsAlreadyAccepted] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [authErrorMessage, setAuthErrorMessage] = useState<string | null>(
    null,
  );
  const [lockoutState, setLockoutState] = useState<LoginLockoutState>(
    DEFAULT_LOGIN_LOCKOUT_STATE,
  );
  const [lockoutSeconds, setLockoutSeconds] = useState(0);
  const [lockoutReady, setLockoutReady] = useState(false);
  const [loginFailed, setLoginFailed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const biometricInProgress = useRef(false);

  const {
    login,
    loginWithBiometrics,
    isLoading,
    error,
    clearError,
    biometryType,
    biometricsEnabled,
  } = useAuth();

  const isBusy = isLoading || isSubmitting || !lockoutReady;
  const biometryDisplayName = getBiometryDisplayName(biometryType);

  useEffect(() => {
    let active = true;
    loadLoginLockoutState().then(restored => {
      if (!active) {
        return;
      }
      setLockoutState(restored);
      setLockoutSeconds(
        getRemainingLockoutSeconds(restored.lockedUntil),
      );
      if (
        !restored.lockedUntil &&
        restored.remainingAttempts < MAX_LOGIN_ATTEMPTS
      ) {
        setAuthErrorMessage(
          formatRemainingAttempts(restored.remainingAttempts),
        );
        setLoginFailed(true);
      }
      setLockoutReady(true);
    });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!lockoutState.lockedUntil) {
      setLockoutSeconds(0);
      return;
    }

    const refreshTimer = () => {
      const remaining = getRemainingLockoutSeconds(lockoutState.lockedUntil);
      setLockoutSeconds(remaining);
      if (remaining === 0) {
        setLockoutState(DEFAULT_LOGIN_LOCKOUT_STATE);
        setAuthErrorMessage(null);
        setLoginFailed(false);
        clearError();
        clearLoginLockoutState();
      }
    };

    refreshTimer();
    const timer = setInterval(refreshTimer, 1000);
    const subscription = AppState.addEventListener('change', nextState => {
      if (nextState === 'active') {
        refreshTimer();
      }
    });

    return () => {
      clearInterval(timer);
      subscription.remove();
    };
  }, [lockoutState.lockedUntil, clearError]);

  // Check if terms were already accepted on mount
  useEffect(() => {
    const checkTermsAccepted = async () => {
      const accepted = await hasAcceptedTerms();
      if (accepted) {
        setTermsAlreadyAccepted(true);
        setAgreedToTerms(true);
      }
    };
    checkTermsAccepted();
  }, []);

  // Handle biometric login (manual trigger from button)
  const handleBiometricLogin = useCallback(async () => {
    if (isBusy || biometricInProgress.current) {
      return;
    }

    biometricInProgress.current = true;
    clearError();
    setLocalError(null);

    try {
      const success = await loginWithBiometrics();
      if (success) {
        setLockoutState(DEFAULT_LOGIN_LOCKOUT_STATE);
        await clearLoginLockoutState();
      } else {
        console.log('Biometric login failed or cancelled');
      }
    } finally {
      biometricInProgress.current = false;
    }
  }, [isBusy, loginWithBiometrics, clearError]);

  // NOTE: Auto-trigger disabled to prevent loops on some devices
  // Users can tap the fingerprint button to login with biometrics

  const updateNotificationStatus = useCallback(
    (status: LoginLockoutNotificationStatus) => {
      setLockoutState(previous => {
        const next = {...previous, notificationStatus: status};
        persistLoginLockoutState(next);
        return next;
      });
    },
    [],
  );

  const notifyPersonalManager = useCallback(
    async (lastLogin: string) => {
      try {
        await reportLoginLockout(lastLogin);
        updateNotificationStatus('sent');
      } catch (notificationError) {
        console.warn(
          '[LoginScreen] manager notification failed:',
          notificationError,
        );
        updateNotificationStatus('failed');
      }
    },
    [updateNotificationStatus],
  );

  const handleLoginInputChange = (value: string) => {
    setLoginInput(value);
    saveLoginDraft(value, password);
    if (loginFailed) {
      setLoginFailed(false);
    }
  };

  const handlePasswordChange = (value: string) => {
    setPassword(value);
    saveLoginDraft(loginInput, value);
    if (loginFailed) {
      setLoginFailed(false);
    }
  };

  const handleLogin = async () => {
    // Clear previous errors
    setLocalError(null);
    setAuthErrorMessage(null);
    clearError();

    if (
      lockoutState.lockedUntil &&
      getRemainingLockoutSeconds(lockoutState.lockedUntil) > 0
    ) {
      return;
    }

    // Validate inputs
    if (!loginInput.trim()) {
      setLocalError('Please enter your login');
      setLoginFailed(true);
      return;
    }
    if (!password) {
      setLocalError('Please enter your password');
      setLoginFailed(true);
      return;
    }
    if (!agreedToTerms) {
      setLocalError('Please agree to Terms and Privacy Policy');
      setLoginFailed(true);
      return;
    }

    try {
      setIsSubmitting(true);
      saveLoginDraft(loginInput, password);
      await login(loginInput.trim(), password);
      // Save credentials for post-logout prefill (RAM only)
      saveCredentialsForPrefill(loginInput.trim(), password);
      // Mark terms as accepted after successful login
      if (!termsAlreadyAccepted) {
        await setHasAcceptedTerms();
      }

      setLoginFailed(false);
      setLockoutState(DEFAULT_LOGIN_LOCKOUT_STATE);
      await clearLoginLockoutState();
      // Navigation will be handled by App.tsx based on auth state
      // Biometric prompt will be shown on Dashboard
    } catch (err) {
      console.log('Login error:', err);
      if (isCountedLoginError(err)) {
        const next = applyCountedLoginFailure(lockoutState);
        setLoginFailed(true);
        setLockoutState(next);
        setLockoutSeconds(getRemainingLockoutSeconds(next.lockedUntil));
        setAuthErrorMessage(formatRemainingAttempts(next.remainingAttempts));
        await persistLoginLockoutState(next);

        if (next.lockedUntil) {
          Keyboard.dismiss();
          notifyPersonalManager(loginInput.trim());
        }
      } else {
        setLoginFailed(false);
        setAuthErrorMessage(
          err instanceof Error ? err.message : 'Login failed',
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const displayError = localError || authErrorMessage || error;
  const inputHasError = loginFailed;

  const EnvIndicator = () => {
    let bgColor: string | undefined;
    let borderColor: string;
    if (!API_BASE_URL) {
      bgColor = undefined;
      borderColor = '#FF3B30';
    } else if (API_BASE_URL === 'https://api.l2io.ai') {
      bgColor = '#34C759';
      borderColor = '#34C759';
    } else if (API_BASE_URL === 'https://api.test.l2io.ai') {
      bgColor = '#FF9500';
      borderColor = '#FF9500';
    } else {
      bgColor = '#FF3B30';
      borderColor = '#FF3B30';
    }
    return (
      <View
        style={[
          styles.envIndicator,
          {
            backgroundColor: bgColor ?? 'transparent',
            borderColor,
          },
        ]}
      />
    );
  };

  const isLoginLocked =
    lockoutReady &&
    lockoutState.lockedUntil !== null &&
    getRemainingLockoutSeconds(lockoutState.lockedUntil) > 0;

  if (isLoginLocked) {
    return (
      <LoginLockoutScreen remainingSeconds={Math.max(1, lockoutSeconds)} />
    );
  }

  return (
    <KeyboardAvoidingView
      style={styles.keyboardAvoid}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
    >
      <SafeAreaView style={styles.container}>
        <ScrollView
          contentContainerStyle={styles.scrollContent}
          keyboardShouldPersistTaps="handled"
          keyboardDismissMode="on-drag"
          showsVerticalScrollIndicator={false}
          bounces={false}
        >
          <View style={styles.content}>
            {/* Title */}
            <Text style={styles.title}>Hello!</Text>

        {/* Form Section */}
        <View style={styles.formSection}>
          {/* Login Input */}
          <View
            style={[
              styles.inputWrapper,
              inputHasError && styles.inputWrapperError,
            ]}>
            <TextInput
              style={styles.input}
              placeholder="Login"
              placeholderTextColor={COLORS.blueSteel}
              value={loginInput}
              onChangeText={handleLoginInputChange}
              autoCapitalize="none"
              autoCorrect={false}
              editable={!isBusy}
            />
          </View>

          {/* Password Input */}
          <PasswordInput
            value={password}
            onChangeText={handlePasswordChange}
            placeholder="Password"
            editable={!isBusy}
            hasError={inputHasError}
          />

            {/* Error Message Below Inputs */}
            {displayError && (
              <View style={styles.inlineErrorContainer}>
                <Text style={styles.errorText}>{displayError}</Text>
              </View>
            )}

{/* Terms Checkbox - only show if not already accepted */}
          {/* {!termsAlreadyAccepted && ( */}
            <Pressable
              style={styles.checkboxRow}
              onPress={() => setAgreedToTerms(!agreedToTerms)}
              disabled={isBusy}>
              <View style={[styles.checkbox, agreedToTerms && styles.checkboxChecked]}>
                {agreedToTerms && <Text style={styles.checkmark}>✓</Text>}
              </View>
              <Text style={styles.termsText}>
                I agree with{' '}
                <Text
                  style={styles.termsLink}
                  onPress={() => Linking.openURL('https://www.l2io.ai/terms-conditions.html')}>
                  Terms and Privacy Policy
                </Text>
              </Text>
            </Pressable>
           {/* )} */}

          {/* Login Button */}
          <TouchableOpacity
            style={[styles.loginButton, isBusy && styles.loginButtonDisabled]}
            onPress={handleLogin}
            disabled={isBusy}>
            {isBusy ? (
              <ActivityIndicator color={COLORS.white} />
            ) : (
              <Text style={styles.loginButtonText}>Login</Text>
            )}
          </TouchableOpacity>

          {/* Biometric Login Button */}
          {biometricsEnabled && (
            <TouchableOpacity
              style={[styles.biometricButton, isBusy && styles.loginButtonDisabled]}
              onPress={handleBiometricLogin}
              disabled={isBusy}>
              <View style={styles.biometricIconContainer}>
                {biometryType === 'FaceID' ? (
                  <View style={styles.faceIdIcon}>
                    <View style={styles.faceIdFrame} />
                    <View style={styles.faceIdEyes}>
                      <View style={styles.faceIdEye} />
                      <View style={styles.faceIdEye} />
                    </View>
                  </View>
                ) : (
                  <View style={styles.fingerprintIcon}>
                    <View style={styles.fingerprintOuter} />
                    <View style={styles.fingerprintInner} />
                  </View>
                )}
              </View>
              <Text style={styles.biometricButtonText}>
                Login with {biometryDisplayName}
              </Text>
            </TouchableOpacity>
          )}

        </View>

          </View>

          {/* Forgot Password */}
          <TouchableOpacity
            style={styles.forgotPasswordContainer}
            onPress={onForgotPassword}
            disabled={isBusy}>
            <Text style={styles.forgotPasswordText}>Forgot password?</Text>
          </TouchableOpacity>

          {/* Version & Env Indicator */}
          <View style={styles.bottomInfo}>
            <Text style={styles.versionText}>v.{DeviceInfo.getVersion()}</Text>
            <EnvIndicator />
          </View>
        </ScrollView>
      </SafeAreaView>
    </KeyboardAvoidingView>
  );
};

const styles = StyleSheet.create({
  keyboardAvoid: {
    flex: 1,
    backgroundColor: COLORS.white,
  },
  container: {
    flex: 1,
  },
  envIndicator: {
    width: 10,
    height: 10,
    borderRadius: 5,
    borderWidth: 1.5,
  },
  bottomInfo: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 8,
    marginBottom: -20,
  },
  versionText: {
    fontSize: 14,
    color: COLORS.blueSteel,
    fontFamily: FONTS.body,
  },
  scrollContent: {
    flexGrow: 1,
    paddingHorizontal: 24,
    paddingVertical: 40,
  },
  content: {
    flex: 1,
    justifyContent: 'center',
  },
  title: {
    fontFamily: FONTS.title,
    fontSize: 48,
    fontWeight: '400',
    lineHeight: 57,
    color: COLORS.deepNavy,
    marginBottom: 32,
  },
  errorContainer: {
    backgroundColor: COLORS_WRONG.errorBg,
    borderRadius: 8,
    padding: 12,
    borderWidth: 1,
    borderColor: COLORS_WRONG.errorBorder,
    marginBottom: 16,
  },
  errorText: {
    fontFamily: FONTS.body,
    fontSize: 16,
    color: COLORS.brandOrange,
    textAlign: 'left',
  },
  formSection: {
    gap: 16,
  },
  inputWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: COLORS.borderMuted,
    borderRadius: 8,
    height: 56,
    paddingHorizontal: 16,
    backgroundColor: COLORS.white,
  },
  inputWrapperError: {
    borderColor: COLORS.brandOrange,
    borderWidth: 2,
  },
  input: {
    flex: 1,
    fontFamily: FONTS.body,
    fontSize: 18,
    color: COLORS.deepNavy,
    height: '100%',
  },
  inlineErrorContainer: {
    borderColor: COLORS.brandOrange,
    marginTop: -4,
  },
  loginButton: {
    backgroundColor: COLORS.brandOrange,
    borderRadius: 8,
    height: 52,
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 8,
  },
  loginButtonDisabled: {
    opacity: 0.7,
  },
  loginButtonText: {
    lineHeight: 22,
    fontFamily: 'Inter',
    color: COLORS.white,
    fontSize: 16,
    fontWeight: '500',
  },
  checkboxRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 4,
  },
  checkbox: {
    width: 20,
    height: 20,
    borderWidth: 1.5,
    borderColor: COLORS.borderMuted,
    borderRadius: 4,
    marginRight: 10,
    justifyContent: 'center',
    alignItems: 'center',
  },
  checkboxChecked: {
    backgroundColor: COLORS.brandOrange,
    borderColor: COLORS.brandOrange,
  },
  checkmark: {
    color: COLORS.white,
    fontSize: 14,
    fontWeight: 'bold',
  },
  termsText: {
    fontFamily: FONTS.body,
    fontSize: 16,
    color: COLORS.deepNavy,
  },
  termsLink: {
    color: COLORS.deepNavy,
    fontWeight: '600',
  },
  spacer: {
    flex: 1,
  },
  forgotPasswordContainer: {
    alignItems: 'center',
    paddingVertical: 16,
  },
  forgotPasswordText: {
    fontFamily: FONTS.body,
    fontSize: 18,
    color: COLORS.deepNavy,
  },
  biometricButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: COLORS.white,
    borderRadius: 8,
    height: 52,
    borderWidth: 1.5,
    borderColor: COLORS.deepNavy,
    gap: 12,
  },
  biometricButtonText: {
    lineHeight: 22,
    fontFamily: 'Inter',
    color: COLORS.deepNavy,
    fontSize: 16,
    fontWeight: '500',
  },
  biometricIconContainer: {
    width: 28,
    height: 28,
    justifyContent: 'center',
    alignItems: 'center',
  },
  faceIdIcon: {
    width: 24,
    height: 24,
    justifyContent: 'center',
    alignItems: 'center',
  },
  faceIdFrame: {
    width: 22,
    height: 22,
    borderWidth: 2,
    borderColor: COLORS.deepNavy,
    borderRadius: 6,
  },
  faceIdEyes: {
    position: 'absolute',
    flexDirection: 'row',
    gap: 6,
    top: 7,
  },
  faceIdEye: {
    width: 3,
    height: 3,
    backgroundColor: COLORS.deepNavy,
    borderRadius: 1.5,
  },
  fingerprintIcon: {
    width: 24,
    height: 24,
    justifyContent: 'center',
    alignItems: 'center',
  },
  fingerprintOuter: {
    width: 20,
    height: 24,
    borderWidth: 2,
    borderColor: COLORS.deepNavy,
    borderRadius: 10,
  },
  fingerprintInner: {
    position: 'absolute',
    width: 10,
    height: 14,
    borderWidth: 2,
    borderColor: COLORS.deepNavy,
    borderRadius: 5,
  },
});

export default LoginScreen;
